// src/components/aiTalentDrive/DriveReels.tsx
// "Scroll the Drive": short creator reels on /ai-talent-drive, shown as a
// studio monitor beside a list of every reel. Read-only: it GETs the
// public list (babblebots-monolith companyapp/drive_reels_views.py) and
// draws it; polls every minute while the tab is visible so a reel published
// from admin appears without a reload. Renders nothing until there is at
// least one reel.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface DriveReel {
  public_id: string;
  title: string;
  creator: string;
  caption: string;
  duration: number | null;
  published_at: string | null;
  video_url: string;
  video_url_720: string | null;
  poster_url: string;
  captions_url: string | null;
}

const POLL_MS = 60_000;
// A reel published within this window wears the NEW tag
const NEW_FOR_MS = 48 * 60 * 60 * 1000;

const isReel = (value: unknown): value is DriveReel => {
  if (!value || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.public_id === "string" &&
    typeof r.title === "string" &&
    typeof r.video_url === "string" &&
    typeof r.poster_url === "string"
  );
};

const pad = (n: number) => String(n).padStart(2, "0");
const clock = (seconds: number) => `${pad(Math.floor(seconds / 60))}:${pad(Math.floor(seconds % 60))}`;

// Phones on mobile data get the smaller rendition; Save-Data also means no autoplay
const connection = () => {
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string; type?: string };
  };
  const c = nav.connection;
  const saveData = !!c?.saveData;
  const slow = !!c && (c.type === "cellular" || /(^|[^4-9])[23]g$/.test(c.effectiveType || ""));
  return { saveData, prefer720: saveData || slow };
};

interface Props {
  apiBase: string;
}

export default function DriveReels({ apiBase }: Props) {
  const [reels, setReels] = useState<DriveReel[] | null>(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const video = useRef<HTMLVideoElement | null>(null);
  const section = useRef<HTMLElement | null>(null);
  const inView = useRef(false);
  const known = useRef<Set<string>>(new Set());
  // Set once the visitor has tapped anywhere: from then on sound is allowed
  const interacted = useRef(false);
  const { saveData, prefer720 } = useMemo(connection, []);
  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const [failed, setFailed] = useState<string | null>(null);
  // Custom theater overlay, not the browser's Fullscreen API: that stretches
  // a 9:16 clip to fill a landscape screen instead of keeping it centered at
  // its own aspect ratio.
  const [theaterOpen, setTheaterOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/hiring-circuit/reels/`);
      if (!res.ok) return;
      const body = (await res.json()) as { reels?: unknown[] };
      const incoming = (Array.isArray(body?.reels) ? body.reels : []).filter(isReel);
      setReels((previous) => {
        if (previous === null) {
          known.current = new Set(incoming.map((r) => r.public_id));
          return incoming;
        }
        // Keep the order the visitor sees; put reels we have not shown in front,
        // drop ones that were unpublished, refresh the rest in place
        const byId = new Map(incoming.map((r) => [r.public_id, r]));
        const fresh = incoming.filter((r) => !known.current.has(r.public_id));
        fresh.forEach((r) => known.current.add(r.public_id));
        const kept = previous.filter((r) => byId.has(r.public_id)).map((r) => byId.get(r.public_id)!);
        const next = [...fresh, ...kept];
        if (fresh.length) setActive((i) => Math.min(i + fresh.length, next.length - 1));
        return next;
      });
    } catch {
      // first failure: nothing renders; later ones keep what is on screen
    }
  }, [apiBase]);

  useEffect(() => {
    load();
    const tick = () => {
      if (document.visibilityState === "visible") load();
    };
    const timer = window.setInterval(tick, POLL_MS);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", tick);
    };
  }, [load]);

  useEffect(() => {
    const mark = () => {
      interacted.current = true;
    };
    window.addEventListener("pointerdown", mark, { once: true, passive: true });
    window.addEventListener("keydown", mark, { once: true });
    return () => {
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
    };
  }, []);

  const count = reels?.length ?? 0;
  // Clamped: a reel unpublished while in the monitor must not blank the section
  const index = count ? Math.min(active, count - 1) : 0;
  const current = reels?.[index] ?? null;

  // Try with sound first; if the browser refuses, play muted and say so.
  // Reads el.muted directly rather than the `muted` state: a stale closure
  // re-asserting an old value here is what quietly undid the sound toggle.
  const play = useCallback(async () => {
    const el = video.current;
    if (!el) return;
    try {
      await el.play();
      setPlaying(true);
      return;
    } catch {
      /* fall through */
    }
    if (!el.muted) {
      el.muted = true;
      setMuted(true);
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    }
  }, []);

  const pause = () => {
    video.current?.pause();
    setPlaying(false);
  };

  // A hidden tab must not keep talking
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) pause();
      else if (inView.current && !saveData) play();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveData, play]);

  // Plays when the monitor is on screen, pauses when scrolled away
  useEffect(() => {
    const el = section.current;
    if (!el || !current || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        inView.current = visible;
        if (visible && !saveData) play();
        if (!visible) pause();
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.public_id, saveData]);

  // A new reel in the monitor: load it and continue playing if we were
  useEffect(() => {
    const el = video.current;
    if (!el || !current) return;
    setProgress(0);
    setElapsed(0);
    setFailed(null);
    el.load();
    if (inView.current && !saveData) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.public_id]);

  const go = useCallback(
    (index: number) => {
      if (!count) return;
      setActive(((index % count) + count) % count);
    },
    [count]
  );

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      go(index + 1);
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      go(index - 1);
    } else if (event.key === " ") {
      event.preventDefault();
      playing ? pause() : play();
    }
  };

  // Escape closes the theater overlay; a document listener rather than one
  // bound to the section's own focus, since the overlay covers everything
  // and nothing in particular needs to hold focus for it.
  useEffect(() => {
    if (!theaterOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTheaterOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [theaterOpen]);

  const toggleSound = (event: React.MouseEvent) => {
    event.stopPropagation();
    const el = video.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    setMuted(next);
    // Re-assert play() inside this same click handler regardless of the
    // `playing` state already on hand: some mobile browsers only reliably
    // commit a sound change when it rides along with a play() call in the
    // very same user-gesture call stack, not as a bare property flip on an
    // element that merely happens to still be playing from an earlier,
    // separate (autoplay) call. play() on an already-playing element is a
    // harmless no-op.
    el.play()
      .then(() => setPlaying(true))
      .catch(() => {
        /* nothing to do: still playing, or still blocked either way */
      });
  };

  if (!reels || count === 0 || !current) return null;

  const src = prefer720 && current.video_url_720 ? current.video_url_720 : current.video_url;
  const isNew = (r: DriveReel) =>
    !!r.published_at && Date.now() - new Date(r.published_at).getTime() < NEW_FOR_MS;
  const total = current.duration ?? Math.floor(video.current?.duration || 0);

  return (
    <section
      ref={section}
      className="dr-section"
      aria-labelledby="dr-title"
      tabIndex={0}
      onKeyDown={onKey}
    >
      <div className="section-head section-head--center">
        <h2 id="dr-title">
          Scroll the <em>Drive</em>
        </h2>
      </div>

      {theaterOpen && (
        <div
          className="dr-theater-backdrop"
          onClick={() => setTheaterOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="dr-bay">
        <div className="dr-monitor">
          <div
            className={`dr-screen${theaterOpen ? " theater" : ""}`}
            onClick={() => (playing ? pause() : play())}
          >
            <video
              ref={video}
              key={current.public_id}
              poster={current.poster_url}
              preload={saveData ? "none" : "metadata"}
              playsInline
              onTimeUpdate={(e) => {
                const v = e.currentTarget;
                if (v.duration) setProgress(v.currentTime / v.duration);
                setElapsed(v.currentTime);
              }}
              onEnded={() => {
                if (!reducedMotion) go(index + 1);
              }}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onError={() => {
                // The file could not be fetched or decoded: say so on the
                // poster and, with more reels, move on after a moment
                setPlaying(false);
                setFailed(current.public_id);
                if (count > 1) window.setTimeout(() => go(index + 1), 2500);
              }}
            >
              <source src={src} type="video/mp4" />
              {current.captions_url && (
                <track kind="captions" src={current.captions_url} srcLang="en" label="English" />
              )}
            </video>
            <div className="dr-scan" aria-hidden="true" />
            <span className="dr-corner tl" aria-hidden="true" />
            <span className="dr-corner tr" aria-hidden="true" />
            <span className="dr-corner bl" aria-hidden="true" />
            <span className="dr-corner br" aria-hidden="true" />
            <button
              type="button"
              className="dr-expand"
              aria-label={theaterOpen ? "Exit full screen" : "Full screen"}
              onClick={(e) => {
                e.stopPropagation();
                setTheaterOpen((open) => !open);
              }}
            >
              {theaterOpen ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 6l12 12M18 6L6 18"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3m11 0h3a2 2 0 0 0 2-2v-3"
                  />
                </svg>
              )}
            </button>
            {theaterOpen && count > 1 && (
              <div className="dr-nav">
                <button
                  type="button"
                  aria-label="Previous reel"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(index - 1);
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ transform: "rotate(90deg)" }}>
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 6l-6 6 6 6"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Next reel"
                  onClick={(e) => {
                    e.stopPropagation();
                    go(index + 1);
                  }}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ transform: "rotate(90deg)" }}>
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 6l6 6-6 6"
                    />
                  </svg>
                </button>
              </div>
            )}
            <div className="dr-rec mono">
              <span className={`dr-rdot${playing ? " on" : ""}`} aria-hidden="true" />
              REEL {pad(index + 1)} <span className="sep">·</span> {clock(elapsed)} / {clock(total)}
            </div>
            {failed === current.public_id && (
              <div className="dr-failed mono" role="status">
                This reel couldn't load{count > 1 ? " · next one coming up" : ""}
              </div>
            )}
            {!playing && failed !== current.public_id && (
              <button type="button" className="dr-play" aria-label="Play">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="currentColor" d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
            <div className="dr-caption">
              <div className="dr-cap-title">{current.title}</div>
              {current.creator && (
                <div className="dr-cap-by">by {current.creator}</div>
              )}
              {current.caption && <div className="dr-cap-text">{current.caption}</div>}
            </div>
            <button
              type="button"
              className="dr-mute mono"
              onClick={toggleSound}
              aria-pressed={!muted}
            >
              {muted ? "Tap for sound" : "Sound on"}
            </button>
          </div>
          {/* Hidden in theater mode: the real screen has moved to a fixed
              overlay, and an empty strip left behind in its old spot would
              look like a rendering glitch rather than a progress bar. */}
          {!theaterOpen && (
            <div className="dr-trace" aria-hidden="true">
              <span style={{ width: `${progress * 100}%` }} />
            </div>
          )}
        </div>

        <div className="dr-strip">
          <div className="dr-strip-head">
            <span className="mono dr-count">
              {count} {count === 1 ? "reel" : "reels"}
            </span>
          </div>
          <ul className="dr-frames" role="listbox" aria-label="Reels">
            {reels.map((reel, i) => (
              <li
                key={reel.public_id}
                role="option"
                aria-selected={i === index}
                className={`dr-fr${i === index ? " on" : ""}`}
                onClick={() => go(i)}
              >
                <div className="dr-th" style={{ backgroundImage: `url(${reel.poster_url})` }} />
                <div className="dr-info">
                  <div className="dr-meta mono">
                    <span className="dr-num">REEL {pad(i + 1)}</span>
                    {isNew(reel) && <span className="dr-new mono">NEW</span>}
                  </div>
                  <div className="dr-who">
                    {reel.title}
                    {reel.creator && <em> · {reel.creator}</em>}
                  </div>
                </div>
                <span className="dr-tc mono">
                  {reel.duration != null ? clock(reel.duration) : "--:--"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
